// undian.view — menu Undian Doorprize Survei: konfigurasi + undi acak + serah hadiah (→ Beban).
import { esc, rp, dtLocal } from '@domain/format';
import { toast } from '@ui/toast';
import { currentUser } from '@core/session';
import { undian } from '@core/settings';
import { listOutlets } from '@modules/karyawan/karyawan.repo';
import * as repo from './undian.repo';

const canManage = (): boolean => ['Owner', 'Manager'].includes(currentUser()?.role ?? '');

export async function renderUndian(root: HTMLElement): Promise<void> {
  const mgr = canManage();
  root.innerHTML = `<div class="ttl">🎁 Undian Survei</div><div class="panel"><div class="empty">Memuat…</div></div>`;
  let outs: { kode: string; nama: string }[] = [];
  try { outs = await listOutlets(); } catch { /* selektor cabang opsional */ }
  const now = new Date();
  const state = { periode: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`, outlet: 'ALL' };

  async function paint(): Promise<void> {
    const cfg = undian();
    let draws: repo.Draw[] = [], peserta: repo.Peserta[] = [];
    try {
      draws = await repo.listDraws(state.periode);
      if (mgr) peserta = await repo.listPeserta(state.periode, state.outlet); // daftar (HP masked) + sumber animasi
    } catch (e) { root.innerHTML = `<div class="ttl">🎁 Undian Survei</div><div class="panel err">${esc((e as Error).message)}</div>`; return; }
    const jml = peserta.length;

    const outOpt = (sel: string): string => `<option value="ALL" ${sel === 'ALL' ? 'selected' : ''}>Semua cabang (Pusat)</option>`
      + outs.map((o) => `<option value="${esc(o.kode)}" ${sel === o.kode ? 'selected' : ''}>${esc(o.nama)}</option>`).join('');
    const outName = (k: string | null): string => k ? (outs.find((o) => o.kode === k)?.nama ?? k) : 'Pusat';

    // Konfigurasi program dipindah ke Pengaturan → 🎁 Poin & Undian. Di sini hanya info status.
    const konfig = `<div class="scope-note">Program undian: <b>${cfg.on ? 'AKTIF' : 'nonaktif'}</b>${cfg.hadiah ? ' · Hadiah: ' + esc(cfg.hadiah) + (cfg.nilai ? ' (' + rp(cfg.nilai) + ')' : '') : ''}.${mgr ? ' Ubah setelan di <b>Pengaturan → 🎁 Poin &amp; Undian</b>.' : ' (Ubah: Owner/Manager.)'}</div>`;

    // --- Panel undi ---
    const undiPanel = mgr ? `<div class="panel" style="margin-bottom:12px">
      <div class="ttl" style="font-size:15px">🎲 Undi Pemenang</div>
      <div class="toolbar" style="margin:8px 0;gap:8px;flex-wrap:wrap">
        <input type="month" id="u_periode" value="${esc(state.periode)}">
        <select id="u_outlet">${outOpt(state.outlet)}</select>
        <span class="pill" style="background:var(--ice);color:var(--navy)">Peserta: ${jml}</span>
        <button class="btn primary" id="u_undi" ${jml === 0 ? 'disabled' : ''}>🎁 Undi Sekarang</button>
      </div>
      <div class="scope-note">Pemenang dipilih ACAK oleh server (adil) dari pelanggan yang mengisi survei pada periode ini. Undi bisa saat live (nama berputar → berhenti di pemenang) — bukti (link video) diisi di baris pemenang. Pemenang lama tak diundi ulang.</div>
      ${peserta.length ? `<details style="margin-top:8px"><summary style="cursor:pointer;font-size:13px;color:var(--navy);font-weight:600">👥 Lihat ${peserta.length} peserta (No. HP disamarkan)</summary>
        <div style="max-height:220px;overflow:auto;border:1px solid var(--line);border-radius:8px;margin-top:6px">
          <table class="data"><thead><tr><th style="width:32px">#</th><th>Nama</th><th>No. HP</th><th>No. Servis</th></tr></thead><tbody>${peserta.map((p, i) => `<tr><td style="color:var(--muted)">${i + 1}</td><td><b>${esc(p.nama)}</b></td><td>${esc(p.hp)}</td><td class="kecil">${esc(p.no)}</td></tr>`).join('')}</tbody></table>
        </div></details>` : '<div class="scope-note" style="margin-top:6px">Belum ada peserta pada periode ini.</div>'}
    </div>` : '';

    // --- Daftar pemenang periode ---
    const rows = draws.map((d) => {
      const st = d.diserahkan
        ? `<span class="pill" style="background:var(--okbg);color:var(--ok)">✓ Diserahkan</span>`
        : `<span class="pill" style="background:var(--warnbg);color:var(--warn)">Belum diserahkan</span>`;
      const isPath = !!d.bukti && !/^https?:\/\//i.test(d.bukti); // path storage vs link luar
      // Video bukti hanya O/M (bucket 'undian' RLS = is_mgr) → non-O/M tak diberi tombol yg pasti gagal.
      const buktiBtn = !mgr ? '—'
        : d.bukti
          ? (isPath
              ? `<button class="btn" data-vidlihat="${esc(d.bukti)}" style="padding:4px 8px">▶️ Video</button> <button class="btn" data-vidhapus="${d.id}" data-path="${esc(d.bukti)}" style="padding:4px 8px" title="Hapus video">🗑️</button>`
              : `<a href="${esc(d.bukti)}" target="_blank" rel="noopener" class="btn" style="padding:4px 8px">🔗 Bukti</a>`)
          : `<button class="btn" data-vidup="${d.id}" style="padding:4px 8px">🎬 Upload Video</button>`;
      const serahBtn = mgr && !d.diserahkan ? ` <button class="btn primary" data-serah="${d.id}" style="padding:4px 8px">🎁 Serahkan</button>` : '';
      return `<tr><td><b>${esc(d.winner_nama ?? '—')}</b><div class="kecil">${esc(d.winner_hp ?? '')}${d.winner_ticket ? ' · ' + esc(d.winner_ticket) : ''}</div></td>
        <td>${esc(d.hadiah)}${d.nilai ? '<div class="kecil">' + rp(d.nilai) + '</div>' : ''}</td>
        <td>${esc(outName(d.outlet))}</td><td>${d.jml_peserta}</td>
        <td class="kecil">${esc(dtLocal(d.diundi_at))}${d.diserahkan && d.serah_at ? '<br>serah: ' + esc(dtLocal(d.serah_at)) : ''}</td>
        <td>${st}</td><td style="white-space:nowrap">${buktiBtn}${serahBtn}</td></tr>`;
    }).join('') || `<tr><td colspan="7" class="empty">Belum ada pemenang diundi untuk periode ini.</td></tr>`;

    root.innerHTML = `<div class="ttl">🎁 Undian Survei</div>
      ${konfig}${undiPanel}
      <div class="panel"><div class="ttl" style="font-size:15px">🏆 Pemenang &amp; Riwayat — ${esc(state.periode)}</div>
        <table class="data"><thead><tr><th>Pemenang</th><th>Hadiah</th><th>Cabang</th><th>Peserta</th><th>Waktu Undi</th><th>Status</th><th>Aksi</th></tr></thead><tbody>${rows}</tbody></table></div>`;

    // wiring
    if (mgr) {
      const pEl = root.querySelector<HTMLInputElement>('#u_periode');
      const oEl = root.querySelector<HTMLSelectElement>('#u_outlet');
      pEl?.addEventListener('change', () => { if (pEl.value) { state.periode = pEl.value; void paint(); } });
      oEl?.addEventListener('change', () => { state.outlet = oEl.value; void paint(); });
      root.querySelector('#u_undi')?.addEventListener('click', async () => {
        const btn = root.querySelector<HTMLButtonElement>('#u_undi'); if (btn) { btn.disabled = true; btn.textContent = 'Mengundi…'; }
        // Buka tab dulu (dalam gesture klik) supaya tak diblok popup, lalu arahkan setelah pemenang lahir.
        const w = window.open('', '_blank');
        try {
          const r = await repo.undi(state.periode, state.outlet); // pemenang dipilih SERVER (adil)
          if (!r.ok || !r.id) { w?.close(); toast(r.pesan ?? 'Gagal mengundi.'); if (btn) { btn.disabled = false; btn.textContent = '🎁 Undi Sekarang'; } return; }
          const url = `${import.meta.env.BASE_URL}undi.html#draw=${r.id}`;
          // JANGAN 'noopener': tab undi menyalin sesi login dari window.opener (sesi per-tab).
          const tab = w ?? window.open(url, '_blank');
          if (w) w.location.href = url;
          toast(tab ? '🎉 Pemenang diundi — animasi & rekaman di tab baru' : '🎉 Pemenang diundi. Popup diblokir browser — buka menu ini lagi & unggah video manual bila perlu.');
          void paint();
        } catch (e) { w?.close(); toast((e as Error).message); const b = root.querySelector<HTMLButtonElement>('#u_undi'); if (b) { b.disabled = false; b.textContent = '🎁 Undi Sekarang'; } }
      });
      root.querySelectorAll<HTMLElement>('button[data-vidup]').forEach((b) => b.addEventListener('click', () => {
        const id = +(b.dataset.vidup ?? 0);
        const inp = document.createElement('input'); inp.type = 'file'; inp.accept = 'video/*';
        inp.addEventListener('change', async () => {
          const f = inp.files?.[0]; if (!f) return;
          (b as HTMLButtonElement).disabled = true; b.textContent = 'Mengunggah…';
          try { await repo.simpanBuktiVideo(id, f); toast('🎬 Video bukti tersimpan'); void paint(); }
          catch (e) { toast((e as Error).message); (b as HTMLButtonElement).disabled = false; b.textContent = '🎬 Upload Video'; }
        });
        inp.click();
      }));
      root.querySelectorAll<HTMLElement>('button[data-vidlihat]').forEach((b) => b.addEventListener('click', async () => {
        try { const url = await repo.buktiVideoUrl(b.dataset.vidlihat ?? ''); window.open(url, '_blank', 'noopener'); }
        catch (e) { toast((e as Error).message); }
      }));
      root.querySelectorAll<HTMLElement>('button[data-vidhapus]').forEach((b) => b.addEventListener('click', async () => {
        if (!confirm('Hapus video bukti undi ini?')) return;
        try { await repo.hapusBuktiVideo(+(b.dataset.vidhapus ?? 0), b.dataset.path ?? ''); toast('🗑️ Video dihapus'); void paint(); }
        catch (e) { toast((e as Error).message); }
      }));
      root.querySelectorAll<HTMLElement>('button[data-serah]').forEach((b) => b.addEventListener('click', async () => {
        const d = draws.find((x) => x.id === +(b.dataset.serah ?? 0)); if (!d) return;
        if (!confirm(`Serahkan hadiah "${d.hadiah}" ke ${d.winner_nama ?? 'pemenang'}?\nAkan dicatat sebagai Beban ${rp(d.nilai || 0)} (${undian().kategori || 'Hadiah Loyalti'}).`)) return;
        (b as HTMLButtonElement).disabled = true; // cegah klik-dobel → Beban dobel (guard atomik ada di repo)
        try { await repo.serahkan(d); toast('🎁 Hadiah diserahkan + Beban dicatat'); void paint(); } catch (e) { toast((e as Error).message); (b as HTMLButtonElement).disabled = false; }
      }));
    }
  }
  await paint();
}
